import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { PaymentProviderDialog } from '../components/PaymentProviderDialog';
import { Button } from '../components/ui/button';
import { Check, ChevronRight, Sparkles } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const PsychologyCategoryPage = () => {
  const { categoryId } = useParams();
  const { t, language } = useLanguage();
  const { user } = useAuth();
  const navigate = useNavigate();
  const isRTL = language === 'ar';

  const [category, setCategory] = useState(null);
  const [packages, setPackages] = useState([]);
  const [notFound, setNotFound] = useState(false);
  const [dialogPkg, setDialogPkg] = useState(null);

  useEffect(() => {
    setNotFound(false);
    axios
      .get(`${API}/psychology/categories/${categoryId}`)
      .then((r) => setCategory(r.data))
      .catch(() => setNotFound(true));
    axios.get(`${API}/psychology/packages`).then((r) => setPackages(r.data || []));
  }, [categoryId]);

  const bookPackage = (pkg) => {
    if (!user) {
      navigate(`/login?next=${encodeURIComponent(`/psychology/${categoryId}`)}`);
      return;
    }
    // Remember the sub-category so the post-payment redirect can filter the
    // doctor list to only the specialists that match.
    try { localStorage.setItem('tababa_psy_category', categoryId); } catch { /* ignore */ }
    setDialogPkg(pkg);
  };

  if (notFound) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <div className="text-center">
          <p className="text-gray-500 mb-4">{t('القسم غير موجود', 'Category not found')}</p>
          <Link to="/psychology" className="text-[#1e88e5] hover:underline">
            {t('العودة إلى الأقسام', 'Back to categories')}
          </Link>
        </div>
      </div>
    );
  }

  const chevron = isRTL ? 'rotate-180' : '';

  return (
    <div className={`min-h-screen bg-gradient-to-b from-blue-50 via-white to-white ${isRTL ? 'rtl' : 'ltr'}`}>
      {/* Category header */}
      <section className="pt-10 pb-6 px-4">
        <div className="max-w-5xl mx-auto">
          <nav className="flex items-center gap-1 text-sm text-gray-500 mb-4">
            <Link to="/" className="hover:text-[#1e88e5]">{t('الرئيسية', 'Home')}</Link>
            <ChevronRight size={14} className={chevron} />
            <Link to="/psychology" className="hover:text-[#1e88e5]">{t('الصحة النفسية', 'Psychology')}</Link>
            <ChevronRight size={14} className={chevron} />
            <span className="text-gray-700 truncate">{category && (language === 'ar' ? category.title_ar : category.title_en)}</span>
          </nav>

          <div className="bg-white rounded-3xl p-6 md:p-10 shadow-sm border border-gray-100" data-testid="psychology-category-hero">
            <h1 className="text-2xl md:text-3xl font-extrabold text-[#0d2c54] mb-3" data-testid="psychology-category-title">
              {category ? (language === 'ar' ? category.title_ar : category.title_en) : '…'}
            </h1>
            <p className="text-base md:text-lg text-gray-700 leading-relaxed max-w-3xl">
              {category ? (language === 'ar' ? category.description_ar : category.description_en) : ''}
            </p>
          </div>
        </div>
      </section>

      {/* Packages */}
      <section className="pb-16 px-4">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-xl md:text-2xl font-extrabold text-[#1e88e5] mb-8 text-center" data-testid="psychology-packages-title">
            {t('اختر الباقة المناسبة', 'Choose the right package', 'Choisissez votre forfait')}
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8" data-testid="psychology-packages-grid">
            {packages.map((pkg) => {
              const features = language === 'ar' ? (pkg.features_ar || []) : (pkg.features_en || []);
              const billingLabel = language === 'ar' ? pkg.billing_label_ar : pkg.billing_label_en;
              return (
                <div
                  key={pkg.id}
                  data-testid={`psychology-package-${pkg.id}`}
                  className={`relative bg-white rounded-3xl p-6 md:p-8 shadow-sm border transition hover:shadow-xl hover:-translate-y-1
                    ${pkg.popular ? 'border-[#1e88e5] ring-2 ring-[#1e88e5]/20 md:scale-[1.02]' : 'border-gray-100'}
                  `}
                >
                  {pkg.popular && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 flex items-center gap-1 bg-[#1e88e5] text-white text-xs font-bold px-3 py-1 rounded-full shadow" data-testid={`badge-popular-${pkg.id}`}>
                      <Sparkles size={12} /> {t('الأكثر طلباً', 'Most popular', 'Populaire')}
                    </div>
                  )}

                  <h3 className={`text-xl md:text-2xl font-extrabold text-center mb-2 ${pkg.popular ? 'text-[#1e88e5]' : 'text-[#4a6486]'}`}>
                    {language === 'ar' ? pkg.name_ar : pkg.name_en}
                  </h3>

                  <div className="text-center mb-6">
                    <div className="inline-flex items-baseline gap-1">
                      <span className="text-4xl md:text-5xl font-extrabold text-[#e91e63]">${pkg.price}</span>
                    </div>
                    <div className="text-sm text-gray-500 mt-1">
                      {billingLabel ? `${language === 'ar' ? 'دولار ' : ''}${billingLabel}` : `${pkg.currency || 'USD'}`}
                    </div>
                  </div>

                  <ul className="space-y-3 mb-8 min-h-[180px]">
                    {features.map((f, i) => (
                      <li key={`${pkg.id || pkg._id || 'pkg'}-${i}-${f}`} className="flex items-start gap-2 text-sm text-gray-700">
                        <Check size={16} className="text-[#1e88e5] shrink-0 mt-0.5" />
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>

                  <Button
                    onClick={() => bookPackage(pkg)}
                    className={`w-full rounded-full py-5 text-base font-bold shadow
                      ${pkg.popular
                        ? 'bg-[#1e88e5] hover:bg-[#1976d2]'
                        : 'bg-[#5c9ecf] hover:bg-[#4a89b8]'
                      }`}
                    data-testid={`book-psychology-${pkg.id}`}
                  >
                    {t('احجز الآن', 'Book Now', 'Réserver')}
                  </Button>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <PaymentProviderDialog
        open={!!dialogPkg}
        onOpenChange={(v) => { if (!v) setDialogPkg(null); }}
        packageId={dialogPkg?.id}
        packageKind="psychology"
        packageNameEn={dialogPkg?.name_en}
        packageNameAr={dialogPkg?.name_ar}
        amount={dialogPkg?.price}
      />
    </div>
  );
};

export default PsychologyCategoryPage;
